"use client";

import { useState, useRef } from "react";
import { Box, Avatar, CircularProgress, Link } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  ConfirmButton,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import { AITextField } from "@components/shared/form/ai";
import { createRecruitingPrompt } from "@/lib/agent/utils/candidateToPrompt";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";

export default function EditProjects({ onClose, open, project, onUpdate, onDelete, candidate }) {
  const [projectData, setProjectData] = useState({
    name: project?.name || "",
    role: project?.role || "",
    description: project?.description || "",
    image: project?.image || "",
    image_data: null,
  });
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    setProjectData({ ...projectData, [event.target.name]: event.target.value });
  };

  const handleInputAIChange = (field) => (value) => {
    setProjectData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onUpdate({ ...project, ...projectData });
    onClose();
  };

  const handleUploadImageClick = (event) => {
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleImageRemove = (event) => {
    event.preventDefault();
    setProjectData((prev) => ({ ...prev, image: "", image_data: null }));
  };

  const handleUpdateImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProjectData((prev) => ({ ...prev, image: reader.result, image_data: file }));
      setImageLoading(false);
    };
    reader.onerror = () => setImageLoading(false);
    reader.readAsDataURL(file);
  };

  const handleDeleteProject = () => {
    onDelete(project.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit project details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <FormSection>
              <FormFieldTitle>Project name:</FormFieldTitle>
              <TextField fullWidth name="name" variant="outlined" value={projectData.name} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Your role:</FormFieldTitle>
              <TextField fullWidth name="role" variant="outlined" value={projectData.role} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Description:</FormFieldTitle>
              <AITextField
                value={projectData.description || ""}
                onChange={handleInputAIChange("description")}
                variant="textarea"
                placeholder="Describe this project and your contributions..."
                prompt={
                  createRecruitingPrompt(candidate, "general") +
                  `. This is a project description for "${projectData.name || "a project"}" where the candidate's role was "${projectData.role || "contributor"}". Write a compelling description that highlights the project's scope, technologies used, the candidate's specific contributions, and the impact or outcomes achieved. Focus on quantifiable results and technical/professional growth. Make it suitable for recruiting presentations.`
                }
                minRows={4}
                maxRows={8}
                fullWidth
              />
            </FormSection>
            <FormSection sx={{ flexDirection: "row", display: "flex", gap: "16px" }}>
              <FormFieldTitle>Project logo:</FormFieldTitle>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>
                {!imageLoading && (
                  <Avatar src={projectData.image} alt={projectData.name} sx={{ width: 84, height: 84, fontSize: "32px", fontWeight: 600, backgroundColor: "#e0e7ff" }}>
                    {projectData.name.substring(0, 2)}
                  </Avatar>
                )}
                {imageLoading && <CircularProgress size={80} color="secondary" />}
                {!imageLoading && !projectData.image && (
                  <Link href="#" onClick={handleUploadImageClick}>Upload image</Link>
                )}
                {!imageLoading && projectData.image && (
                  <Link href="#" onClick={handleImageRemove}>Remove</Link>
                )}
                <input hidden accept="image/*" type="file" onChange={handleUpdateImage} ref={fileInputRef} />
              </Box>
            </FormSection>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between" }}>
            <ConfirmButton text="Delete project" onUpdate={handleDeleteProject} />
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
